// src/api/install-auth-interceptors.ts
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { apiClient } from '@/api/http'
import type { useUserStore } from '@/stores/user'

type UserStore = ReturnType<typeof useUserStore>

type AuthProblem = {
  code?: string
}

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _authenticationRetry?: boolean
  _skipAuthentication?: boolean
}

function isAuthenticationEndpoint(url: string | undefined): boolean {
  if (!url) {
    return false
  }

  return (
    url.endsWith('/api/login_check') ||
    url.endsWith('/api/token/refresh') ||
    url.endsWith('/api/logout')
  )
}

export function installAuthInterceptors(userStore: UserStore): () => void {
  const requestInterceptor = apiClient.interceptors.request.use(
    async (config: RetryableRequestConfig) => {
      if (config._skipAuthentication || isAuthenticationEndpoint(config.url)) {
        return config
      }

      const authenticated = await userStore.ensureAuthenticated()

      if (!authenticated) {
        throw new axios.CanceledError('Er is geen geldige sessie beschikbaar.')
      }

      return config
    },
  )

  const responseInterceptor = apiClient.interceptors.response.use(
    (response) => response,

    async (error: AxiosError<AuthProblem>) => {
      const config = error.config as RetryableRequestConfig | undefined

      const accessTokenExpired =
        error.response?.status === 401 && error.response.data?.code === 'ACCESS_TOKEN_EXPIRED'

      if (
        !config ||
        config._authenticationRetry ||
        config._skipAuthentication ||
        isAuthenticationEndpoint(config.url) ||
        !accessTokenExpired
      ) {
        throw error
      }

      config._authenticationRetry = true

      const refreshed = await userStore.refreshSession()

      if (!refreshed) {
        throw error
      }

      return apiClient.request(config)
    },
  )

  return () => {
    apiClient.interceptors.request.eject(requestInterceptor)

    apiClient.interceptors.response.eject(responseInterceptor)
  }
}
